import React, { useState, useCallback, useContext, useEffect } from 'react';
import { Handle, Position, useReactFlow, useEdges, useNodes } from 'reactflow';
import { ExclamationTriangleIcon, Pencil1Icon } from '@radix-ui/react-icons';
import { PiMinusSquareBold } from "react-icons/pi";
import { GrConfigure } from 'react-icons/gr';
import './LossSubtractorNode.css'; // Import the CSS file
import LossSubtractorConfigModal from './LossSubtractorConfigModal';
import { usePipeline } from '../../../context/PipelineContext';
import { NodeMappingContext } from '../../Canvas/Canvas';
import { reconfigureNode } from '../../../api/api';
import { notifyError } from '../../../errors/notifyError';
import { OP } from '../../../api/operations';

const LossSubtractorNode = ({ id, data, selected }) => {
  const { transmissionLossPerc, wheelingLossPerc, lossPerc, colName, name } = data;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfiguring, setIsConfiguring] = useState(false);
  const reactFlowInstance = useReactFlow();
  const edges = useEdges();
  const nodes = useNodes();
  const { session, addLossSubtractorNode, updateSelectedNode } = usePipeline();
  const { updateNodeMapping } = useContext(NodeMappingContext);
  
  // Log the data received by the component for debugging
  useEffect(() => {
    console.log(`LossSubtractorNode ${id} received data:`, data);
    // Log specific column name information for debugging
    console.log(`LossSubtractorNode ${id} column name details:`, {
      colNameFromProps: colName,
      colNameFromData: data.colName,
      colNameType: typeof data.colName,
      isEmpty: data.colName === '',
      isUndefined: data.colName === undefined
    });
  }, [id, data, colName]);

  // Check if node is configured properly
  // With auto-select, colName may be blank; treat presence of a name and any loss % as sufficient
  const effectiveLossPerc = (transmissionLossPerc ?? 0) + (wheelingLossPerc ?? 0) || lossPerc;
  const isConfigured = data.isConfigured || (!!name && effectiveLossPerc !== undefined && effectiveLossPerc !== '');
  
  // Check if this node has an input connection
  const hasInputConnection = edges.some(edge => edge.target === id);
  
  // Get the node mapping from context
  const { getNodeMapping } = useContext(NodeMappingContext);
  
  // Get the input node name if there's a connection
  const getInputNodeName = useCallback(() => {
    const inputEdge = edges.find(edge => edge.target === id);
    if (!inputEdge) return null;
    
    const sourceNodeId = inputEdge.source;
    const sourceNode = nodes.find(node => node.id === sourceNodeId);
    
    if (!sourceNode) return null;
    
    // First try to get the name from the node data
    if (sourceNode.data && sourceNode.data.name) {
      return sourceNode.data.name;
    }
    
    // If not found in data, try to get it from the node mapping
    const mappedName = getNodeMapping()[sourceNodeId];
    if (mappedName) {
      return mappedName;
    }
    
    // If all else fails, use the node ID
    return sourceNodeId;
  }, [edges, id, nodes, getNodeMapping]);
  
  // Get the current input node name
  const inputNodeName = getInputNodeName();
  
  // Check if the node needs reconfiguration
  // A node needs reconfiguration if:
  // 1. It's explicitly marked as needing reconfiguration (isApiConfigured === false)
  // 2. It was previously configured but now has no input connection
  // 3. It's configured but the input node has changed
  const needsReconfiguration = 
    // Explicitly marked as needing reconfiguration
    data.isApiConfigured === false || 
    // Was configured but now has no input connection
    (isConfigured && !hasInputConnection && data.inputNodeName) ||
    // Input node has changed from what was previously configured
    (isConfigured && data.inputNodeName && inputNodeName && data.inputNodeName !== inputNodeName);
  
  // Log node state for debugging
  useEffect(() => {
    console.log(`LossSubtractorNode ${id} state:`, {
      isConfigured,
      needsReconfiguration,
      name,
      lossPerc,
      colName,
      hasInputConnection,
      inputNodeName,
      storedInputNodeName: data.inputNodeName,
      dataIsConfigured: data.isConfigured,
      dataIsApiConfigured: data.isApiConfigured
    });
  }, [id, isConfigured, needsReconfiguration, name, effectiveLossPerc, colName, data, hasInputConnection, inputNodeName]);
  
  // Function to handle reconfiguration of the input node
  const handleReconfigureInput = async () => {
    if (!session.pipeline_name || !name) return;
    
    try {
      setIsConfiguring(true);
      
      // Get the current input node name
      const inputNodeName = getInputNodeName();
      
      if (!inputNodeName) {
        throw new Error('No input node connected');
      }
      
      console.log(`Reconfiguring node ${name} with input node ${inputNodeName}`);
      console.log(`Previous input node was: ${data.inputNodeName || 'unknown'}`);
      
      // Call the API to update the input node
      await reconfigureNode(
        session.pipeline_name,
        name,
        inputNodeName
      );
      
      console.log(`Node ${name} input reconfigured successfully from ${data.inputNodeName || 'unknown'} to ${inputNodeName}`);
      
      // Update the node data to mark it as configured
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                isConfigured: true,
                isApiConfigured: true,
                inputNodeName: inputNodeName
              }
            };
          }
          return node;
        })
      );
      
      // Also update the session state
      const position = getNodePosition();
      
      addLossSubtractorNode(
        name,
        lossPerc,
        colName,
        inputNodeName,
        { x: position.x, y: position.y }
      );
      
      setIsConfiguring(false);
    } catch (error) {
      console.error('Error reconfiguring node input:', error);
      notifyError(error, { operation: OP.node.reconfigure });
      setIsConfiguring(false);
    }
  };

  // Get the current node position
  const getNodePosition = useCallback(() => {
    const node = nodes.find(n => n.id === id);
    return node ? node.position : { x: 0, y: 0 };
  }, [nodes, id]);

  const openConfigModal = useCallback(() => {
    // Log the current state for debugging
    console.log("openConfigModal called with state:", {
      hasInputConnection,
      isConfigured,
      needsReconfiguration,
      lossPerc,
      colName,
      isModalOpen
    });
    
    if (!hasInputConnection && !isConfigured) {
      // Show an alert if there's no input connection and we're not already configured
      // This allows editing an existing node even if the input is temporarily disconnected
      notifyError(new Error("Please connect an input to this loss subtractor node before configuring it."), {
        toastId: 'loss.connectInput',
      });
      return;
    }
    
    // If the node is already configured and the user is trying to edit it
    // We now allow editing since we've implemented the edit functionality
    if (isConfigured && !needsReconfiguration) {
      console.log("Opening edit modal for configured node");
      // Continue to open the modal for editing
    }
    
    // Open the full configuration modal
    console.log(`Opening modal for ${needsReconfiguration ? 'reconfiguration' : isConfigured ? 'editing' : 'configuration'}`);
    
    // Force a small delay to ensure React state updates properly
    setTimeout(() => {
      setIsModalOpen(true);
      console.log("Modal should be open now:", { isModalOpen: true });
    }, 50);
  }, [hasInputConnection, isConfigured, needsReconfiguration, lossPerc, colName, isModalOpen]);

  const closeConfigModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleConfigured = useCallback(async (configData) => {
    // Start the configuration process
    setIsConfiguring(true);
    
    try {
      const { name, transmissionLossPerc, wheelingLossPerc, lossPerc, colName } = configData;
      const inputNodeName = getInputNodeName();
      const position = getNodePosition();
      
      // Log the configuration data for debugging
      console.log(`Configuring loss subtractor node ${id} -> ${name}:`, {
        configData,
        inputNodeName,
        position,
        edges: edges.filter(edge => edge.target === id || edge.source === id),
        isConfigured: data.isConfigured,
        isApiConfigured: data.isApiConfigured,
        needsReconfiguration
      });
      
      // Add the loss subtractor node to the session state
      if (inputNodeName) {
        // First update the node ID to name mapping before adding to session
        // This ensures the correct name is used in the pipeline state
        updateNodeMapping(id, name);
        console.log(`Updated node mapping before adding to session: ${id} -> ${name}`);
        
        // Find the source node in the nodes array to get its actual name
        const inputEdge = edges.find(edge => edge.target === id);
        const sourceNodeId = inputEdge ? inputEdge.source : null;
        const sourceNode = sourceNodeId ? nodes.find(node => node.id === sourceNodeId) : null;
        
        // Determine the actual input node name to use
        let actualInputNodeName = inputNodeName;
        
        // If the source node has a name in its data, use that
        if (sourceNode && sourceNode.data && sourceNode.data.name) {
          actualInputNodeName = sourceNode.data.name;
          console.log(`Using source node name from data: ${actualInputNodeName}`);
        }
        
        // Check if this is a reconfiguration (node already exists but input changed)
        // Only consider it a reconfiguration if needsReconfiguration is true
        // This prevents treating regular edits as reconfigurations
        const isReconfiguration = needsReconfiguration;
        
        console.log(`${isReconfiguration ? 'Reconfiguring' : 'Adding'} loss subtractor node to session:`, {
          name,
          transmissionLossPerc,
          wheelingLossPerc,
          lossPerc,
          colName,
          inputNodeName: actualInputNodeName,
          position,
          isReconfiguration
        });
        
        // Check if this is a first-time configuration, a reconfiguration, or an edit
        const isFirstTimeConfig = !data.isConfigured && !data.isApiConfigured;
        const isEditingExistingNode = data.isConfigured && !needsReconfiguration;
        
        console.log('Configuration type:', {
          isFirstTimeConfig,
          isReconfiguration,
          isEditingExistingNode,
          isConfigured: data.isConfigured,
          isApiConfigured: data.isApiConfigured,
          needsReconfiguration
        });
        
        // For first-time configuration, the modal component has already added the node to the pipeline
        if (isFirstTimeConfig && session.pipeline_name) {
          console.log(`First-time configuration for node ${name} with input ${actualInputNodeName}`);
          // No need to call the API here, it was already called by the modal component
        }
        // If this is a reconfiguration, use the reconfigure API
        else if (isReconfiguration && session.pipeline_name) {
          console.log(`Calling reconfigure API for node ${name} with new source ${actualInputNodeName}`);
          
          try {
            console.log(`Reconfiguring node ${name} with new input:`, {
              oldInput: data.inputNodeName,
              newInput: actualInputNodeName
            });
            
            // Call the API to reconfigure the node
            await reconfigureNode(
              session.pipeline_name,
              name,
              actualInputNodeName
            );
            
            console.log(`Node ${name} reconfigured successfully with new input ${actualInputNodeName}`);
          } catch (reconfigError) {
            console.error('Error reconfiguring node:', reconfigError);
            // Don't continue with local updates if API call fails
            throw reconfigError; // Re-throw the error to be caught by the outer try-catch
          }
        }
        // If this is an edit of an existing node, we don't need to call any API here
        // The editLossSubtractorNode API was already called by the LossSubtractorConfigModal component
        else if (isEditingExistingNode) {
          console.log(`Editing existing node ${name} - API call already handled by modal`);
        }
        
        // Now add/update the node in the session with the correct name
        addLossSubtractorNode(
          name,
          (parseFloat(transmissionLossPerc || 0) + parseFloat(wheelingLossPerc || 0)).toString(),
          colName,
          actualInputNodeName,
          { x: position.x, y: position.y }
        );
        
        // Update the node data with the new configuration
        reactFlowInstance.setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  name,
                  transmissionLossPerc,
                  wheelingLossPerc,
                  lossPerc: (parseFloat(transmissionLossPerc || 0) + parseFloat(wheelingLossPerc || 0)).toString(),
                  colName,
                  isConfigured: true,
                  isApiConfigured: true,
                  inputNodeName: actualInputNodeName // Store the input node name for future reference
                }
              };
            }
            return node;
          })
        );
        
        // If the node was previously selected, update the selection with the new name
        if (session.selected_node === id) {
          console.log(`Node was selected, updating selection from ${id} to ${name}`);
          updateSelectedNode(name);
        }
      } else {
        // Just update the UI if we're not connected to a backend
        // Still update the mapping first
        updateNodeMapping(id, name);
        console.log(`Updated node mapping in local mode: ${id} -> ${name}`);
        
        reactFlowInstance.setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  name,
                  transmissionLossPerc,
                  wheelingLossPerc,
                  lossPerc: (parseFloat(transmissionLossPerc || 0) + parseFloat(wheelingLossPerc || 0)).toString(),
                  colName,
                  isConfigured: true,
                  isApiConfigured: true,
                  inputNodeName: null // No input node in this case
                }
              };
            }
            return node;
          })
        );
        
        // Update selection if needed
        if (session.selected_node === id) {
          updateSelectedNode(name);
        }
      }
    } catch (error) {
      console.error('Error configuring loss subtractor node:', error);
      notifyError(error, { operation: needsReconfiguration ? OP.node.reconfigure : OP.node.editLossSubtractor });
    } finally {
      setIsConfiguring(false);
      closeConfigModal();
    }
  }, [id, reactFlowInstance, getInputNodeName, getNodePosition, closeConfigModal, updateNodeMapping, addLossSubtractorNode, session, updateSelectedNode, edges, nodes, data, needsReconfiguration]);

  return (
    <>
      <div
        className={`loss-node ${selected ? 'loss-selected' : needsReconfiguration ? 'loss-needs-reconfiguration' : isConfigured ? 'loss-configured' : 'loss-not-configured'}`}
      >
        <div className={`loss-node-header ${needsReconfiguration ? 'loss-needs-reconfiguration' : isConfigured ? 'loss-configured' : 'loss-not-configured'}`}>
          <div className="loss-node-header-title">
            <div className="loss-node-type">Loss Subtractor</div>
            {name && (isConfigured || needsReconfiguration) && <div className="loss-node-name-display">{name}</div>}
          </div>
          <div className="loss-node-header-icons">
            {/* Show different buttons based on node state */}
            {needsReconfiguration ? (
              <button 
                onClick={handleReconfigureInput}
                className="loss-config-button loss-reconfigure-button"
                title="Reconfigure input node"
                disabled={!hasInputConnection || isConfiguring}
              >
                <GrConfigure className="loss-icon" />
              </button>
            ) : (
              <button 
                onClick={openConfigModal}
                className="loss-config-button"
                title={isConfigured ? "Edit Configuration" : hasInputConnection ? "Configure Loss Subtractor" : "Connect an input first"}
                disabled={(!hasInputConnection && !isConfigured) || isConfiguring}
              >
                {isConfigured ? (
                  <Pencil1Icon className="loss-icon" />
                ) : (
                  <ExclamationTriangleIcon className="loss-icon" />
                )}
              </button>
            )}
            <PiMinusSquareBold className="loss-icon" />
          </div>
        </div>
        <div className="loss-node-content">
          <div className="loss-text-xs">
            {!hasInputConnection && (
              <div className="loss-text-warning">
                Connect an input to configure
              </div>
            )}
            {hasInputConnection && !isConfigured && (
              <div className="loss-text-warning">
                Parameters not configured
              </div>
            )}
            {needsReconfiguration && (
              <div className="loss-text-warning">
                {hasInputConnection 
                  ? "Input changed - click spanner to update" 
                  : "Input missing - connect a new input"}
              </div>
            )}
            {isConfiguring && (
              <div className="loss-text-info">
                Configuring...
              </div>
            )}
            {isConfigured && (
              <>
                <div>Loss Percentage: {
                  // Check if lossPerc exists and is not undefined
                  lossPerc !== undefined && lossPerc !== '' ? 
                    lossPerc : 
                    // If not, check if data.lossPerc exists and is not undefined
                    (data.lossPerc !== undefined && data.lossPerc !== '' ? 
                      data.lossPerc : 
                      'N/A')
                }%</div>
                <div>Column: { colName && colName !== 'Column name not specified' ? colName : (data.colName && data.colName !== 'Column name not specified' ? data.colName : 'Auto') }</div>
              </>
            )}
          </div>
          <div className="loss-flex-container">
            <Handle
              type="target"
              position={Position.Left}
              id="input"
              className={`loss-connection-point ${needsReconfiguration ? 'loss-needs-reconfiguration' : isConfigured ? 'loss-configured' : 'loss-not-configured'}`}
              data-maxconnections="1" // Custom attribute to indicate this handle should only have one connection
            />
            <Handle
              type="source"
              position={Position.Right}
              id="output"
              className={`loss-connection-point ${needsReconfiguration ? 'loss-needs-reconfiguration-output' : isConfigured ? 'loss-configured-output' : 'loss-not-configured'}`}
              isConnectable={isConfigured} // Only allow connections if the node is configured
              data-maxconnections="1" // Custom attribute to indicate this handle should only have one connection
            />
          </div>
        </div>
      </div>

      <LossSubtractorConfigModal
        isOpen={isModalOpen}
        onClose={closeConfigModal}
        onConfigured={handleConfigured}
        initialData={{ 
          name, 
          transmissionLossPerc,
          wheelingLossPerc,
          lossPerc,
          colName,
          inputNodeName: getInputNodeName(),
          position: getNodePosition(),
          isReconfiguring: needsReconfiguration,
          isEditing: isConfigured && !needsReconfiguration
        }}
      />
    </>
  );
};

export default LossSubtractorNode;